// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a component for displaying a chronological list of audit events
 * related to a specific person.
 */
import { getPersonAuditEvents } from '@/audit/api/get-person-audit-events';
import { AuditEventListItem } from '@/audit/components/audit-event-list-item';
import { Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';

interface Props {
  /**
   * The key of the person whose audit events should be displayed.
   */
  personKey: string;
}

/**
 * Displays a vertical list of audit events for a specific person.
 * This component suspends while fetching the audit events.
 *
 * Each audit event is displayed using the AuditEventListItem component,
 * which provides a consistent format for displaying event details.
 */
export function PersonAuditList(props: Props) {
  const { data } = useSuspenseQuery({
    queryKey: ['person', props.personKey, 'audit'],
    queryFn: () => getPersonAuditEvents(props.personKey),
    retry: false,
  });

  return (
    <Stack spacing={2}>
      {data.events.map((event) => (
        <AuditEventListItem key={event.id} event={event} />
      ))}
    </Stack>
  );
}
